/* CUSTOM_LEGENDWS_BY_CLAUDE (Ryan 2026-08-10) — Legend weight-stack upgrade → cart.

   The upgrade is a SEPARATE product (one shared add-on carries every upgrade size), so
   buying a machine with an upgrade means putting TWO lines in the cart from one submit.
   Shopify's product form posts a single `id`, and you cannot mix `id` with `items[]`.

   HOW THIS HOOKS IN, AND WHY THIS WAY:
   The theme's product-form.js binds a normal (bubble-phase) submit listener that builds
   FormData, POSTs it, refreshes the cart drawer and runs the after-ATC behaviour. That is
   a lot of working machinery and it is NOT worth reimplementing to squeeze in a second
   line. So instead:

     1. listen in the CAPTURE phase, which runs before the theme's listener
     2. if an upgrade is chosen: stop the event, add ONLY the upgrade via /cart/add.js
     3. set a pass-through flag and re-submit the form

   The second submit sails past this handler and the theme's own listener does exactly what
   it always does — adds the machine, refreshes the drawer, opens it. Nothing in the theme
   is patched, wrapped or duplicated, so a theme update cannot silently break the machine
   half of the purchase; the worst case is that the upgrade stops being added.

   The upgrade goes in FIRST deliberately: if it went second, the drawer would already be
   open showing a machine with no upgrade, and a failure there would be invisible.

   ⚠️ `_sku` carries the SUPPLIER part number ({model}-WS). The leading underscore is
   Shopify's convention for a property hidden from the customer, and po-routes.js:523 reads
   `customAttrs._sku` AHEAD of `variant.sku` on catalog lines — which is the entire reason
   the PO prints `1120-WS` with no PO-builder change. Renaming this property breaks POs
   silently: the order still looks right, the supplier gets the wrong part number. */

(function () {
  'use strict';

  var FLAG = '__legendWsHandled';

  /* The picker sits OUTSIDE the product form — it renders in the buy-buttons block ABOVE the
     freight notice, because the freight notice has to be the last thing read before the buy
     buttons. So it cannot be found with form.querySelector.

     It renders exactly once per product page (verified: the freight badge, its sibling,
     appears once), so a document-level lookup is correct. The strict guard is on the FORM
     instead: the page carries a second <product-form> for quick-add, and matching that one
     would attach this machine's upgrade to a different product's cart line. */
  function blockFor(form) {
    if (!form.classList || !form.classList.contains('js-product-form-main')) return null;
    return document.querySelector('[data-legend-ws]');
  }

  function selectedVariant(block) {
    // custom-select.js mirrors the chosen option into the hidden native <select> and fires a
    // bubbling change (assets/custom-select.js:340-343). Reading the native element means we
    // do not depend on the component's internal state.
    var id = block.getAttribute('data-select-id');
    var native = id ? document.getElementById(id + '-native') : null;
    var v = native && native.value ? parseInt(native.value, 10) : 0;
    return isNaN(v) ? 0 : v;
  }

  function qtyOf(form) {
    var q = form.querySelector('[name="quantity"]');
    var n = q ? parseInt(q.value, 10) : 1;
    return (!isNaN(n) && n > 0) ? n : 1;
  }

  function addUpgrade(block, variantId, quantity) {
    var props = {};
    var model = (block.getAttribute('data-model') || '').trim();
    var machine = (block.getAttribute('data-machine') || '').trim();
    // Hidden from the customer, read by the PO builder. Only stamped when we actually know
    // the model — a bare "-WS" on a PO is worse than no property at all, because it looks
    // like a real part number.
    if (model) props._sku = model + '-WS';
    if (machine) props.For = machine;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity, properties: props }] })
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          throw new Error((data && (data.description || data.message)) || 'Could not add the weight stack upgrade.');
        }
        return data;
      });
    });
  }

  function onSubmit(evt) {
    var form = evt.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

    // Second pass — we already added the upgrade; let the theme do its thing.
    if (form[FLAG]) { form[FLAG] = false; return; }

    var block = blockFor(form);
    if (!block) return;
    var variantId = selectedVariant(block);
    if (!variantId) return;              // standard stack chosen — nothing to do

    evt.preventDefault();
    evt.stopImmediatePropagation();

    var submitBtn = form.querySelector('[name="add"]');
    if (submitBtn) {
      if (submitBtn.getAttribute('aria-disabled') === 'true') return;  // already in flight
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
    }

    addUpgrade(block, variantId, qtyOf(form))
      .then(function () {
        // Hand back to the theme: it adds the machine and owns all the after-ATC UI.
        form[FLAG] = true;
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'false');
          submitBtn.classList.remove('is-loading');
        }
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      })
      .catch(function (err) {
        form[FLAG] = false;
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'false');
          submitBtn.classList.remove('is-loading');
        }
        // Fail loudly and add NOTHING. Silently falling through to "machine only" would
        // ship a customer the wrong stack and nobody would find out until the PO.
        var msg = block.querySelector('.legend-ws__err');
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'legend-ws__err';
          block.appendChild(msg);
        }
        msg.textContent = (err && err.message) || 'Could not add the weight stack upgrade.';
      });
  }

  // Capture phase — must run before product-form.js's own submit listener.
  document.addEventListener('submit', onSubmit, true);
}());
